import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faPen, faPlus } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors.jsx';
import { useTranslation } from 'react-i18next';
import CoilPickerModal from './CoilPickerModal';
import { metresForKg, formatMetres } from '../../utils/coilMath';

/**
 * CoilPickStrip
 *
 * The one-line coil summary that sits under a coil-tracked order line, with the button
 * that opens the picker. It owns the modal's open state and renders CoilPickerModal
 * itself, so a call site is one line of JSX rather than a piece of state, a handler and
 * a modal it has to remember to render.
 *
 * Props:
 *   picks         - array of { coil_id, take_kg, batch_number, colour, kg_per_m,
 *                             remaining_weight_kg, available_weight_kg }
 *   targetKg      - the line quantity in kg, for the short/over badge
 *   disabled      - no warehouse or product chosen yet, so there is nothing to pick from
 *   disabledNote  - why (defaults to the standard "choose a warehouse and a product")
 *   onOpen        - optional, fired when the picker opens
 *   warehouseId   - passed through to the picker
 *   productSizeId - passed through to the picker
 *   onChange      - fn(picks), called when the picker is applied
 *   context       - 'order' | 'loading'
 *   readOnly      - show the picks without letting them be changed
 */

const toKg = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? Math.round(num) : 0;
};

const groupKg = (value) => toKg(value).toLocaleString('en-IN');

const CoilPickStrip = ({
  picks,
  targetKg = 0,
  disabled = false,
  disabledNote,
  onOpen,
  warehouseId,
  productSizeId,
  onChange,
  context = 'order',
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const rows = useMemo(() => (Array.isArray(picks) ? picks : []), [picks]);

  const pickedKg = useMemo(
    () => rows.reduce((sum, pick) => sum + toKg(pick.take_kg), 0),
    [rows]
  );

  // Metres are derived per coil from that coil's own kg-per-metre — there is no single
  // weight-per-metre for the size — so the strip's total is a sum of per-coil lengths.
  const pickedMetres = useMemo(
    () => rows.reduce((sum, pick) => sum + metresForKg(pick, pick.take_kg), 0),
    [rows]
  );

  const target = toKg(targetKg);
  const delta = pickedKg - target;

  // Same reading as the picker's footer: amber under, green on target (within a kilo),
  // blue over. Over-picking is legitimate, so it is never red.
  const deltaBadge = (() => {
    if (!rows.length || target <= 0) return null;
    if (Math.abs(delta) <= 1) {
      return { text: t('coils.picker.summary.exact'), colour: UI_COLORS.STATUS.SUCCESS };
    }
    return delta < 0
      ? { text: t('coils.picker.summary.short', { amount: groupKg(-delta) }), colour: UI_COLORS.STATUS.WARNING }
      : { text: t('coils.picker.summary.over', { amount: groupKg(delta) }), colour: UI_COLORS.STATUS.INFO };
  })();

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
    if (onOpen) onOpen();
  };

  const buttonLabel = readOnly
    ? t('common.buttons.view')
    : (rows.length ? t('coils.line.editCoils') : t('coils.line.selectCoils'));

  // Nothing to view and nothing to change: a read-only line with no coils is just blank.
  if (readOnly && !rows.length) {
    return (
      <div className="flex items-center gap-2 text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
        <FontAwesomeIcon icon={faLayerGroup} />
        {t('coils.line.noneSelected')}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2 min-w-0">
        <FontAwesomeIcon icon={faLayerGroup} className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }} />
        <span className="text-xs font-medium truncate" style={{ color: rows.length ? UI_COLORS.TEXT.PRIMARY : UI_COLORS.TEXT.MUTED }}>
          {rows.length
            ? t('coils.line.summaryWithLength', {
              count: String(rows.length),
              weight: groupKg(pickedKg),
              length: formatMetres(pickedMetres),
            })
            : t('coils.line.noneSelected')}
        </span>
        {deltaBadge && (
          <span
            className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
            style={{
              backgroundColor: colorHelpers.withOpacity(deltaBadge.colour, 0.12),
              color: deltaBadge.colour,
            }}
          >
            {deltaBadge.text}
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        title={disabled ? (disabledNote || t('coils.line.disabledNote')) : undefined}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
        style={{
          backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.TERTIARY, 0.12),
          color: UI_COLORS.BRAND.TERTIARY,
        }}
      >
        <FontAwesomeIcon icon={rows.length ? faPen : faPlus} />
        {buttonLabel}
      </button>

      {disabled && (
        <span className="text-[11px]" style={{ color: UI_COLORS.TEXT.MUTED }}>
          {disabledNote || t('coils.line.disabledNote')}
        </span>
      )}

      {/* Mounted only while open so each closed strip costs nothing and the picker's
          draft state is rebuilt from `picks` on every open. */}
      {isOpen && (
        <CoilPickerModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          warehouseId={warehouseId}
          productSizeId={productSizeId}
          targetKg={target}
          value={rows}
          onChange={onChange}
          context={context}
          readOnly={readOnly}
        />
      )}
    </div>
  );
};

export default CoilPickStrip;
